import { Injectable } from '@angular/core';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

@Injectable({
  providedIn: 'root',
})
export class DocumentExportService {
  private static readonly _crcTable: Uint32Array = (() => {
    const table = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
      let c = n;
      for (let k = 0; k < 8; k++) {
        c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
      }
      table[n] = c >>> 0;
    }
    return table;
  })();

  /**
   * Generates a PDF from provided data and column headers.
   * @param columnNames - An array of column names.
   * @param rowData - An array of row data where each row is an array of string | number values.
   * @param fileName - Name of the PDF file to be saved.
   */
  public exportToPdf(
    columnNames: string[],
    rowData: Array<Array<string | number>>,
    fileName: string
  ): void {
    const doc = new jsPDF('l', 'mm', 'a4');

    autoTable(doc, {
      head: [columnNames],
      body: rowData,
      theme: 'grid',
      headStyles: { fillColor: [128, 128, 128] },
    });

    doc.setTextColor(0);
    doc.save(`${fileName}.pdf`);
  }

  /**
   * Exports data to a CSV file, or a real Excel workbook.
   * @param columnNames - An array of column names.
   * @param rowData - An array of row data where each row is an array of string | number values.
   * @param fileName - Name of the file to be saved (without extension).
   * @param fileType - 'csv' for a plain comma-separated file. 'xls' builds a
   * genuine .xlsx workbook instead of CSV text, because Excel does not
   * reliably parse CSV content saved under a .xls extension: it either
   * warns that the format/extension don't match, or - if opened directly -
   * re-guesses each cell's type from the text and silently turns values
   * that merely look like a date or number (e.g. a campaign named
   * "aug 3") into a different value entirely. A real .xlsx file declares
   * each cell's type explicitly, so Excel never has to guess.
   */
  public exportToCsv(
    columnNames: string[],
    rowData: Array<Array<string | number>>,
    fileName: string,
    fileType: 'csv' | 'xls'
  ): void {
    if (!rowData || rowData.length === 0) {
      console.error('No data available for export.');
      return;
    }

    if (fileType === 'xls') {
      this._exportToExcel(columnNames, rowData, fileName);
      return;
    }

    // Convert column names and data into CSV format
    const csvContent = [
      columnNames.join(','), // Add headers
      ...rowData.map(
        (row) => row.map((value) => `"${value}"`).join(',') // Enclose values in quotes to handle commas
      ),
    ].join('\n');

    // Create a Blob with CSV data
    const blob = new Blob([csvContent], { type: 'text/csv' });

    // Create a download link
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileName}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /**
   * Builds a genuine .xlsx workbook and downloads it. Each cell's type
   * comes straight from the JS value we hand it (string stays a string,
   * number stays a number), so Excel displays the exact value with no
   * re-parsing and no warning.
   *
   * An .xlsx file is just a ZIP archive of a small set of XML parts, so
   * this writes that ZIP by hand (uncompressed/"stored" entries, so no
   * compression algorithm is needed) using only
   * Uint8Array/DataView/TextEncoder - all standard in every browser, no
   * extra dependency required.
   */
  private _exportToExcel(
    columnNames: string[],
    rowData: Array<Array<string | number>>,
    fileName: string
  ): void {
    const bytes = this._buildXlsxBytes(columnNames, rowData, fileName);
    const blob = new Blob([bytes], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileName}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /**
   * Builds the raw bytes of a valid .xlsx file for the given tabular data.
   */
  private _buildXlsxBytes(
    columnNames: string[],
    rowData: Array<Array<string | number>>,
    sheetName: string
  ): Uint8Array {
    const enc = (s: string) => new TextEncoder().encode(s);
    const files: Array<{ name: string; data: Uint8Array }> = [
      {
        name: '[Content_Types].xml',
        data: enc(this._xlsxContentTypesXml()),
      },
      { name: '_rels/.rels', data: enc(this._xlsxRootRelsXml()) },
      {
        name: 'xl/workbook.xml',
        data: enc(this._xlsxWorkbookXml(sheetName)),
      },
      {
        name: 'xl/_rels/workbook.xml.rels',
        data: enc(this._xlsxWorkbookRelsXml()),
      },
      { name: 'xl/styles.xml', data: enc(this._xlsxStylesXml()) },
      {
        name: 'xl/worksheets/sheet1.xml',
        data: enc(this._xlsxSheetXml(columnNames, rowData)),
      },
    ];
    return this._buildZip(files);
  }

  private _xlsxContentTypesXml(): string {
    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/></Types>`;
  }

  private _xlsxRootRelsXml(): string {
    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>`;
  }

  private _xlsxWorkbookRelsXml(): string {
    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/><Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>`;
  }

  private _xlsxStylesXml(): string {
    // fonts: 0 = regular, 1 = bold (used for the header row).
    // cellXfs: 0 = default (unused directly), 1 = center-aligned regular
    // (all data cells), 2 = center-aligned bold (the header row).
    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts><fills count="1"><fill><patternFill patternType="none"/></fill></fills><borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders><cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs><cellXfs count="3"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0" applyAlignment="1"><alignment horizontal="center"/></xf><xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1" applyAlignment="1"><alignment horizontal="center"/></xf></cellXfs><cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles></styleSheet>`;
  }

  private _xlsxWorkbookXml(sheetName: string): string {
    const safeName = this._escapeXml(sheetName).slice(0, 31);
    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="${safeName}" sheetId="1" r:id="rId1"/></sheets></workbook>`;
  }

  private _xlsxSheetXml(
    columnNames: string[],
    rowData: Array<Array<string | number>>
  ): string {
    const allRows: Array<Array<string | number>> = [columnNames, ...rowData];
    const rowsXml = allRows
      .map((row, rowIdx) => {
        const r = rowIdx + 1;
        const isHeaderRow = rowIdx === 0;
        // Style 2 = bold + centered (header row), style 1 = centered only
        // (every data row, both text and number cells) - see _xlsxStylesXml.
        const styleIndex = isHeaderRow ? 2 : 1;
        const cellsXml = row
          .map((value, colIdx) => {
            const ref = `${this._xlsxColLetter(colIdx)}${r}`;
            if (typeof value === 'number' && Number.isFinite(value)) {
              return `<c r="${ref}" s="${styleIndex}"><v>${value}</v></c>`;
            }
            return `<c r="${ref}" s="${styleIndex}" t="inlineStr"><is><t xml:space="preserve">${this._escapeXml(
              value
            )}</t></is></c>`;
          })
          .join('');
        return `<row r="${r}">${cellsXml}</row>`;
      })
      .join('');

    const lastCol = this._xlsxColLetter(
      Math.max(...allRows.map((r) => r.length)) - 1
    );
    const dimension = `A1:${lastCol}${allRows.length}`;

    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><dimension ref="${dimension}"/><sheetViews><sheetView workbookViewId="0"/></sheetViews><sheetData>${rowsXml}</sheetData></worksheet>`;
  }

  /** 0 -> A, 25 -> Z, 26 -> AA, ... */
  private _xlsxColLetter(index: number): string {
    let n = index + 1;
    let letters = '';
    while (n > 0) {
      const rem = (n - 1) % 26;
      letters = String.fromCharCode(65 + rem) + letters;
      n = Math.floor((n - 1) / 26);
    }
    return letters;
  }

  private _escapeXml(value: string | number): string {
    return (
      String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        // eslint-disable-next-line no-control-regex
        .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '')
    ); // strip XML-illegal control chars
  }

  /**
   * Minimal ZIP writer (stored entries only, no compression needed for the
   * small XML parts an .xlsx is made of).
   */
  private _buildZip(
    files: Array<{ name: string; data: Uint8Array }>
  ): Uint8Array {
    const localParts: Uint8Array[] = [];
    const centralParts: Uint8Array[] = [];
    let offset = 0;
    const dosTime = 0;
    const dosDate = 0x21; // fixed, valid MS-DOS date (1980-01-01)

    for (const file of files) {
      const nameBytes = new TextEncoder().encode(file.name);
      const data = file.data;
      const crc = this._crc32(data);

      const local = new DataView(new ArrayBuffer(30));
      local.setUint32(0, 0x04034b50, true); // local file header signature
      local.setUint16(4, 20, true); // version needed
      local.setUint16(6, 0, true); // flags
      local.setUint16(8, 0, true); // compression = stored
      local.setUint16(10, dosTime, true);
      local.setUint16(12, dosDate, true);
      local.setUint32(14, crc, true);
      local.setUint32(18, data.length, true); // compressed size
      local.setUint32(22, data.length, true); // uncompressed size
      local.setUint16(26, nameBytes.length, true);
      local.setUint16(28, 0, true); // extra field length
      const localHeader = new Uint8Array(local.buffer);

      localParts.push(localHeader, nameBytes, data);

      const central = new DataView(new ArrayBuffer(46));
      central.setUint32(0, 0x02014b50, true); // central directory signature
      central.setUint16(4, 20, true); // version made by
      central.setUint16(6, 20, true); // version needed
      central.setUint16(8, 0, true); // flags
      central.setUint16(10, 0, true); // compression = stored
      central.setUint16(12, dosTime, true);
      central.setUint16(14, dosDate, true);
      central.setUint32(16, crc, true);
      central.setUint32(20, data.length, true);
      central.setUint32(24, data.length, true);
      central.setUint16(28, nameBytes.length, true);
      central.setUint16(30, 0, true); // extra field length
      central.setUint16(32, 0, true); // comment length
      central.setUint16(34, 0, true); // disk number start
      central.setUint16(36, 0, true); // internal attrs
      central.setUint32(38, 0, true); // external attrs
      central.setUint32(42, offset, true); // offset of local header
      const centralHeader = new Uint8Array(central.buffer);

      centralParts.push(centralHeader, nameBytes);
      offset += localHeader.length + nameBytes.length + data.length;
    }

    const centralDirStart = offset;
    const centralDir = this._concatBytes(centralParts);

    const eocd = new DataView(new ArrayBuffer(22));
    eocd.setUint32(0, 0x06054b50, true); // end of central directory signature
    eocd.setUint16(4, 0, true);
    eocd.setUint16(6, 0, true);
    eocd.setUint16(8, files.length, true);
    eocd.setUint16(10, files.length, true);
    eocd.setUint32(12, centralDir.length, true);
    eocd.setUint32(16, centralDirStart, true);
    eocd.setUint16(20, 0, true);

    return this._concatBytes([
      ...localParts,
      centralDir,
      new Uint8Array(eocd.buffer),
    ]);
  }

  private _concatBytes(chunks: Uint8Array[]): Uint8Array {
    const total = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
    const out = new Uint8Array(total);
    let offset = 0;
    for (const chunk of chunks) {
      out.set(chunk, offset);
      offset += chunk.length;
    }
    return out;
  }

  private _crc32(bytes: Uint8Array): number {
    let crc = 0xffffffff;
    for (const byte of bytes) {
      crc = DocumentExportService._crcTable[(crc ^ byte) & 0xff] ^ (crc >>> 8);
    }
    return (crc ^ 0xffffffff) >>> 0;
  }
}
